import { useState } from "react";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";

/**
 * Street address with Google Places suggestions, for the redesigned forms.
 * The redesign's answer to LocationInputField in components.js — same
 * library, same stored value — dressed to sit inside an .rf-input-shell.
 *
 * `value` is the text shown; `onChange(json)` receives what gets stored:
 *   - while typing:        JSON.stringify({ name })
 *   - on picking a match:  JSON.stringify({ name, coordinates: { lat, lng } })
 * so a hand-typed address still saves when Google has no match for it, and
 * run sheets and printing read either shape unchanged.
 *
 * Suggestions are limited to Australia. The Maps script (with the places
 * library) is loaded in public/index.html; if it failed to load, this falls
 * back to a plain text field rather than breaking the form.
 */
const SEARCH_OPTIONS = { componentRestrictions: { country: "au" } };

const placesReady = () =>
  Boolean(window.google && window.google.maps && window.google.maps.places);

export default function AddressAutocomplete({ value, placeholder, onChange, disabled }) {
  const [ready] = useState(placesReady);

  if (!ready) {
    return (
      <input
        value={value || ""}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(JSON.stringify({ name: e.target.value }))}
      />
    );
  }

  function pick(address) {
    onChange(JSON.stringify({ name: address }));
    geocodeByAddress(address)
      .then((results) => getLatLng(results[0]))
      .then((coordinates) =>
        onChange(JSON.stringify({ name: address, coordinates }))
      )
      .catch(() => {}); // the name alone is still a usable address
  }

  return (
    <PlacesAutocomplete
      value={value || ""}
      onChange={(text) => onChange(JSON.stringify({ name: text }))}
      onSelect={pick}
      searchOptions={SEARCH_OPTIONS}
      debounce={250}
      highlightFirstSuggestion
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div className="rf-addr">
          <input {...getInputProps({ placeholder, disabled, autoComplete: "off" })} />
          {(loading || suggestions.length > 0) && (
            <div className="rf-addr-list" role="listbox">
              {loading && !suggestions.length && (
                <div className="rf-addr-note">Looking up addresses…</div>
              )}
              {suggestions.map((s) => {
                const main = s.formattedSuggestion || {};
                return (
                  <div
                    {...getSuggestionItemProps(s, {
                      className: `rf-addr-item${s.active ? " is-active" : ""}`,
                    })}
                    key={s.placeId}
                  >
                    <span className="rf-addr-main">
                      {main.mainText || s.description}
                    </span>
                    {main.secondaryText && (
                      <span className="rf-addr-sub">{main.secondaryText}</span>
                    )}
                  </div>
                );
              })}
              <div className="rf-addr-foot">Suggestions from Google · Australia</div>
            </div>
          )}
        </div>
      )}
    </PlacesAutocomplete>
  );
}
